import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type OpenChatState = {
  chatId: number | null;
  peerLabel: string | null;
};

type OpenChatContextValue = OpenChatState & {
  setOpenChat: (next: OpenChatState | null) => void;
};

const OpenChatContext = createContext<OpenChatContextValue>({
  chatId: null,
  peerLabel: null,
  setOpenChat: () => undefined,
});

export const useOpenChat = () => useContext(OpenChatContext);

interface OpenChatProviderProps {
  children: React.ReactNode;
}

export const OpenChatProvider: React.FC<OpenChatProviderProps> = ({ children }) => {
  const [state, setState] = useState<OpenChatState>({ chatId: null, peerLabel: null });

  const setOpenChat = useCallback((next: OpenChatState | null) => {
    if (!next || next.chatId == null) {
      setState({ chatId: null, peerLabel: null });
      return;
    }
    setState({
      chatId: next.chatId,
      peerLabel: next.peerLabel?.trim() || null,
    });
  }, []);

  const value = useMemo(
    () => ({
      chatId: state.chatId,
      peerLabel: state.peerLabel,
      setOpenChat,
    }),
    [setOpenChat, state.chatId, state.peerLabel],
  );

  return <OpenChatContext.Provider value={value}>{children}</OpenChatContext.Provider>;
};
